import type { QueryClient } from '@tanstack/react-query';
import type { AtlasAccessSummary } from '../models/AtlasAccessSummary';
import { atlasAccessSummaryKey } from './useAtlasAccessSummary';

/**
 * Publishes the approval decision for the immutable head just accepted by a
 * succession save. The existing principal-scoped summary remains the
 * authorization boundary; this helper can narrow an enabled action to
 * awaiting approval, but can never elevate a denied action.
 */
export function primeAcceptedSuccessionAccess(input: {
  queryClient: QueryClient;
  principalId: string | null;
  actorOrgId: string | null;
  folderId: string;
  diagramId: string;
  changeSetId: string;
  mayApply: boolean | undefined;
}): void {
  if (input.mayApply === undefined || !input.principalId || !input.actorOrgId) return;
  const targetKey = atlasAccessSummaryKey({
    principalId: input.principalId,
    actorOrgId: input.actorOrgId,
    folderId: input.folderId,
    diagramId: input.diagramId,
  });
  const target = input.queryClient.getQueryData<AtlasAccessSummary>(targetKey);
  if (!target) return;
  const existingApply = target.actions.applyScenario;
  const applyScenario = existingApply.state !== 'enabled'
    ? existingApply
    : input.mayApply
      ? { state: 'enabled' as const }
      : { state: 'pending' as const, reason: 'approval' as const };
  input.queryClient.setQueryData<AtlasAccessSummary>(atlasAccessSummaryKey({
    principalId: input.principalId,
    actorOrgId: input.actorOrgId,
    folderId: input.folderId,
    diagramId: input.diagramId,
    context: { kind: 'scenario', changeSetId: input.changeSetId },
  }), {
    ...target,
    target: { ...target.target, contextKind: 'scenario' },
    actions: { ...target.actions, applyScenario },
    resolvedAt: new Date().toISOString(),
  });
}
